import * as React from 'react'
import styled, { css } from 'styled-components'
import { lighten } from 'polished'
import { theme, COMMON, CommonProps, UIColorTheme, UIColorThemes } from 'global'
import { Icon } from 'components/Icon/Icon'

interface IButtonProps extends CommonProps, UIColorTheme {
  variant?: 'primary' | 'outline' | 'link'
  full?: boolean
  thin?: boolean
  disabled?: boolean
  loading?: boolean
  onClick?: (e: React.SyntheticEvent) => void
  children?: React.ReactNode
  id?: string
  className?: string
  icon?: JSX.Element
}

const colors = {
  dark: theme.colors.kinkyPurple,
  light: theme.colors.white,
}

const inverseColors = {
  dark: theme.colors.white,
  light: theme.colors.kinkyPurple,
}

const colorPicker = (color: 'dark' | 'light' | undefined) =>
  colors[color as keyof typeof colors]
const inverseColorPicker = (color: 'dark' | 'light' | undefined) =>
  inverseColors[color as keyof typeof colors]

const getColor = (uiColorTheme: UIColorThemes, props: IButtonProps) =>
  props.color
    ? props.color
    : props.backgroundColor
    ? colorPicker(uiColorTheme)
    : inverseColorPicker(uiColorTheme)

const getBgColor = (uiColorTheme: UIColorThemes, props: IButtonProps) =>
  props.backgroundColor ? props.backgroundColor : colorPicker(uiColorTheme)

const ButtonStyled = styled.button<IButtonProps>`
  ${COMMON}
  ${({
    disabled,
    full,
    thin,
    uiColorTheme = 'dark',
    variant = 'primary',
    ...props
  }) =>
    css`
      background-color: ${getBgColor(uiColorTheme, props)};
      color: ${getColor(uiColorTheme, props)};
      border-radius: 25px;
      padding: 14px 22px;
      font-size: 17px;
      letter-spacing: 0.4px;
      min-height: ${props.minHeight || '50px'};
      font-weight: bold;
      line-height: 1.2;
      cursor: pointer;

      span {
        display: flex;
        align-items: center;
      }

      &:hover {
        box-shadow: 1px 1px 5px 0px #a0a0a0;
      }

      ${
        variant === 'outline' &&
        css`
          background-color: ${colorPicker('light')};
          border: 1px solid ${inverseColorPicker('light')};
          color: ${inverseColorPicker('light')};
        `
      }

      ${
        variant === 'link' &&
        css`
          font-size: 16px;
          font-weight: 600;
          background: none;
          border-radius: 0;
          &:hover {
            box-shadow: none;
            color: ${lighten(0.2)(getColor(uiColorTheme, props).toString())};
          }
        `
      }

      ${
        full &&
        css`
          width: 100%;
          @media screen and (min-width: ${theme.maxWidths.medium}) {
            width: 50%;
          }
        `
      }

      ${
        thin &&
        css`
          min-height: 45px;
          padding: 10px 20px;
        `
      }

      ${
        disabled &&
        css`
          opacity: 0.5;
          pointer-events: none;
        `
      }
    `}
`

const Loader = styled(Icon)`
  color: inherit;
  margin: 0 !important;
  position: absolute;
`

export const Button = ({
  uiColorTheme,
  loading = false,
  icon,
  children,
  ...props
}: IButtonProps) => {
  const ref = React.useRef<HTMLButtonElement>(null)
  const [forceLoading, setForceLoading] = React.useState(false)

  const _loading = loading || forceLoading

  // Observer class loading & apply loading state. This is used for the SubscriptionModal
  // due to it's limited API, to toggle a loading state with JS.
  React.useEffect(() => {
    if (ref.current) {
      const observer = new MutationObserver((event) => {
        setForceLoading(
          Boolean(
            event.find((e) =>
              (e.target as HTMLElement).classList.contains('loading')
            )
          )
        )
      })

      observer.observe(ref.current, {
        attributes: true,
        attributeFilter: ['class'],
        childList: false,
        characterData: false,
      })
    }
  }, [ref])

  return (
    // TODO: some issue with the html.button and styled-system typings, not sure how to fix.
    // @ts-ignore
    <ButtonStyled ref={ref} uiColorTheme={uiColorTheme} {...props}>
      <>
        {_loading && <Loader name='loader' viewBox='100' />}
        {!_loading && (
          <>
            {icon}
            <span>{children}</span>
          </>
        )}
      </>
    </ButtonStyled>
  )
}
